import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useToast } from '../../../contexts/ToastContext';
import { getAuthHeader } from '../../../services/otpAuthService';
import MentionTextarea from './MentionTextarea';

const API_BASE_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace('/graphql', '')
  : 'http://localhost:5000';

interface NewDiscussionModalProps {
  isOpen: boolean;
  onClose: () => void;
  areaSlug: string;
  areaName: string;
  areaType?: string;
  onCreated: (discussion: any) => void;
}

const NewDiscussionModal: React.FC<NewDiscussionModalProps> = ({
  isOpen,
  onClose,
  areaSlug,
  areaName,
  areaType,
  onCreated,
}) => {
  const toast = useToast();
  const isLounge = areaType === 'lounge';
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [mentions, setMentions] = useState<string[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ title?: string; content?: string }>({});

  const fileInputRef = useRef<HTMLInputElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // ESC to close
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const resetForm = useCallback(() => {
    setTitle('');
    setContent('');
    setMentions([]);
    setFiles([]);
    setErrors({});
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const validate = (): boolean => {
    const e: { title?: string; content?: string } = {};
    if (!isLounge && (!title.trim() || title.trim().length < 5)) {
      e.title = 'Il titolo deve avere almeno 5 caratteri';
    }
    const minContent = isLounge ? 3 : 10;
    if (!content.trim() || content.trim().length < minContent) {
      e.content = `Il contenuto deve avere almeno ${minContent} caratteri`;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async (): Promise<any[]> => {
    if (files.length === 0) return [];

    const attachments: any[] = [];
    const images = files.filter(f => f.type.startsWith('image/'));
    const docs = files.filter(f => !f.type.startsWith('image/'));

    if (images.length > 0) {
      const formData = new FormData();
      images.forEach(f => formData.append('files', f));
      const res = await fetch(`${API_BASE_URL}/api/community/v2/upload/images`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        attachments.push(...(data.data || []));
      }
    }

    if (docs.length > 0) {
      const formData = new FormData();
      docs.forEach(f => formData.append('files', f));
      const res = await fetch(`${API_BASE_URL}/api/community/v2/upload/documents`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        attachments.push(...(data.data || []));
      }
    }

    return attachments;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      // Upload files first
      const attachments = await uploadFiles();

      const res = await fetch(
        `${API_BASE_URL}/api/community/v2/areas/${areaSlug}/discussions`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader(),
          },
          body: JSON.stringify({
            ...(isLounge ? {} : { title: title.trim() }),
            content: content.trim(),
            mentions,
            attachments,
          }),
        }
      );

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message || 'Errore durante la creazione');
      }

      const data = await res.json();
      toast.success('Discussione creata!');
      resetForm();
      onCreated(data.discussion || data);
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Errore durante la creazione della discussione');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === overlayRef.current) handleClose();
      }}
    >
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-800 rounded-xl shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">
            {isLounge ? `Nuovo post in ${areaName}` : `Nuova Discussione in ${areaName}`}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Title - hidden for lounge */}
          {!isLounge && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Titolo
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value.slice(0, 200));
                  if (errors.title) setErrors((prev) => ({ ...prev, title: undefined }));
                }}
                placeholder="Titolo della discussione"
                maxLength={200}
                className={`w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                  errors.title ? 'border-red-400' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              <div className="flex justify-between mt-1">
                {errors.title && (
                  <p className="text-xs text-red-500">{errors.title}</p>
                )}
                <p className="text-xs text-gray-400 ml-auto">{title.length}/200</p>
              </div>
            </div>
          )}

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Contenuto
            </label>
            <MentionTextarea
              value={content}
              onChange={(val) => {
                setContent(val.slice(0, 20000));
                if (errors.content) setErrors((prev) => ({ ...prev, content: undefined }));
              }}
              onMentionsChange={setMentions}
              placeholder={isLounge ? "Cosa vuoi condividere? Usa @ per menzionare" : "Descrivi il tuo argomento... Usa @ per menzionare"}
              rows={8}
              className={`w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[200px] resize-y ${
                errors.content ? 'border-red-400' : 'border-gray-300 dark:border-gray-600'
              }`}
            />
            <div className="flex justify-between mt-1">
              {errors.content && (
                <p className="text-xs text-red-500">{errors.content}</p>
              )}
              <p className="text-xs text-gray-400 ml-auto">{content.length}/20000</p>
            </div>
          </div>

          {/* Attachments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Allegati
            </label>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf"
              onChange={handleFileChange}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
              Allega file
            </button>
            {files.length > 0 && (
              <ul className="mt-2 space-y-1">
                {files.map((file, i) => (
                  <li key={i} className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 px-3 py-1.5 rounded">
                    <span className="truncate mr-2">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="text-red-400 hover:text-red-600 flex-shrink-0"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors inline-flex items-center gap-2"
            >
              {submitting && (
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              )}
              {submitting ? 'Pubblicazione...' : isLounge ? 'Pubblica' : 'Pubblica Discussione'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewDiscussionModal;
